import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  BULLETIN_SCALE,
  BULLETIN_SUBJECTS,
  blockCovers,
  type BulletinSubject,
} from '../lib/bulletin-subjects.ts';
import {
  PERIODS,
  PERIOD_LABEL,
  blockRatio,
  bulletinTotal,
  formatRatio,
} from '../lib/bulletin.ts';
import { getHandwriting } from '../lib/handwriting.ts';
import { frSheetDate, observationPeriod, observationRows } from '../lib/observation-sheet.ts';
import { studentDisplayName, studentNumbers } from '../lib/student-roster.ts';
import { live, useStore } from '../store/store.ts';
import type { Evaluation, Student } from '../store/types.ts';
import { openModal } from '../ui/ui-store.ts';
import { ClassGrid } from '../features/evaluations/ClassGrid.tsx';
import { LessonEvaluationModal } from '../features/evaluations/EvaluationModal.tsx';
import { ObservationModal } from '../features/observations/ObservationModal.tsx';
import { BulkStudentsModal, StudentModal } from '../features/students/StudentModal.tsx';

/** Lazily loads the stylus image; sync keeps it out of the main payload. */
function Handwriting({ observationId }: { observationId: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getHandwriting(observationId).then((value) => {
      if (!cancelled) setSrc(value);
    });
    return () => {
      cancelled = true;
    };
  }, [observationId]);

  if (!src) return null;
  return <img class="handNote" src={src} alt="Note manuscrite" />;
}

/**
 * One line of the pupil's report: a block, and its total for each period.
 *
 * Deliberately the same functions the class grid calls, so a mark read here
 * and a mark read there can never disagree.
 */
interface BlockRow {
  block: BulletinSubject;
  totals: readonly string[];
}

function reportRows(studentId: string, evaluations: readonly Evaluation[]): BlockRow[] {
  const rows: BlockRow[] = [];

  for (const block of BULLETIN_SUBJECTS) {
    const mine = evaluations.filter((e) => blockCovers(block, e.subject));
    if (mine.length === 0) continue;

    rows.push({
      block,
      totals: PERIODS.map((period) =>
        formatRatio(
          blockRatio(studentId, block.domains, mine.filter((e) => e.period === period)),
          BULLETIN_SCALE,
        ),
      ),
    });
  }

  return rows;
}

function StudentDetail({ student, number }: { student: Student; number: number | undefined }) {
  // Filtering happens outside the selector on purpose: a selector returning a
  // freshly built array looks like a changed snapshot to useSyncExternalStore
  // on every render, which re-renders forever.
  const observations = live(useStore((s) => s.observations)).filter(
    (o) => o.studentId === student.id,
  );
  const evaluations = live(useStore((s) => s.evaluations)).filter(
    (e) => e.studentId === student.id,
  );

  const [period, setPeriod] = useState('1');

  const report = useMemo(() => reportRows(student.id, evaluations), [student.id, evaluations]);

  // The headline figure is the mean of the blocks that have one for the chosen
  // period — the same "NC" rule underneath, so it stays blank until there is
  // something honest to show.
  const overall = useMemo(
    () =>
      formatRatio(
        bulletinTotal(
          report.map((row) =>
            blockRatio(
              student.id,
              row.block.domains,
              evaluations.filter((e) => blockCovers(row.block, e.subject) && e.period === period),
            ),
          ),
        ),
        BULLETIN_SCALE,
      ),
    [report, student.id, evaluations, period],
  );

  return (
    <div class="card">
      <div class="cardHead">
        <div class="avatar" style={{ width: '56px', height: '56px' }}>
          {number ?? '—'}
        </div>
        <div>
          <h2>{studentDisplayName(student.name)}</h2>
          <div class="muted">
            {student.birth} {student.philo ? `• ${student.philo}` : ''}
          </div>
        </div>
        <span class="spacer" />
        {/* The grid is the default view, so there has to be a way back to it
            once a pupil has been opened. */}
        <button class="btn soft" onClick={() => useStore.getState().selectStudent(null)}>
          ← Toute la classe
        </button>
        <button
          class="btn"
          onClick={() => openModal('Modifier l’élève', <StudentModal studentId={student.id} />)}
        >
          Modifier
        </button>
      </div>

      <div class="stats" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        <div class="stat">
          <strong>{observations.length}</strong>
          <span class="small muted">Observations</span>
        </div>
        <div class="stat">
          <strong>{evaluations.length}</strong>
          <span class="small muted">Évaluations</span>
        </div>
        <div class="stat">
          <strong>
            {overall}
            {overall !== 'NC' ? ' / 100' : ''}
          </strong>
          <span class="small muted">Moyenne — {PERIOD_LABEL[period]}</span>
        </div>
      </div>

      <div class="row" style={{ margin: '14px 0' }}>
        <button
          class="btn soft"
          onClick={() =>
            openModal('Nouvelle observation', <ObservationModal studentId={student.id} />)
          }
        >
          + Observation
        </button>
        {/* Printing a pupil's file prints the observation sheet rendered below,
            never this screen. */}
        <button class="btn soft" onClick={() => window.print()}>
          Imprimer les observations
        </button>
        <span class="spacer" />
        <select
          class="periodPick"
          value={period}
          aria-label="Période du bulletin"
          onChange={(e) => setPeriod(e.currentTarget.value)}
        >
          {PERIODS.map((p) => (
            <option key={p} value={p}>
              {PERIOD_LABEL[p]}
            </option>
          ))}
        </select>
      </div>

      <div class="detailSection">
        <h3 style={{ marginBottom: '10px' }}>Bulletin</h3>
        {report.length === 0 ? (
          <div class="empty">Aucune évaluation encodée.</div>
        ) : (
          <div class="gridScroll">
            <table class="gradeTable compact">
              <thead>
                <tr>
                  <th class="stickyCol">Matière</th>
                  {PERIODS.map((p) => (
                    <th key={p}>
                      <div>{PERIOD_LABEL[p]}</div>
                      <div class="small muted">/ {BULLETIN_SCALE}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.map((row) => (
                  <tr key={row.block.key}>
                    <th class="stickyCol">{row.block.label}</th>
                    {row.totals.map((total, i) => (
                      <td key={PERIODS[i]} class="gridCell">
                        {total}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div class="grid3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <h3 style={{ marginBottom: '10px' }}>Observations</h3>
          {observations.length ? (
            observations
              .slice()
              .reverse()
              .map((o) => (
                <div key={o.id} class="obs">
                  <div class="row">
                    <b>{o.subject}</b>
                    <span class="spacer" />
                    {/* A quick note guesses its pupil and its subject, so the
                        way back to it has to be on the note itself. */}
                    <button
                      class="btn ghost small"
                      onClick={() =>
                        openModal(
                          'Modifier l’observation',
                          <ObservationModal observationId={o.id} />,
                        )
                      }
                    >
                      Modifier
                    </button>
                  </div>
                  <div class="row" style={{ margin: '6px 0' }}>
                    {o.date && <span class="tag">{o.date}</span>}
                    {o.difficulty && <span class="tag">{o.difficulty}</span>}
                  </div>
                  <div>{o.note}</div>
                  {o.support && (
                    <div class="small muted" style={{ marginTop: '6px' }}>
                      Aide : {o.support}
                    </div>
                  )}
                  {o.hasHandwriting && <Handwriting observationId={o.id} />}
                </div>
              ))
          ) : (
            <div class="empty">Aucune observation.</div>
          )}
        </div>

        <div>
          <h3 style={{ marginBottom: '10px' }}>Évaluations</h3>
          {evaluations.length ? (
            evaluations
              .slice()
              .reverse()
              .map((e) => (
                <div key={e.id} class="eval">
                  <b>{e.title}</b>
                  <div class="row" style={{ margin: '6px 0' }}>
                    <span class="tag">{e.subject}</span>
                    {e.domain && <span class="tag">{e.domain}</span>}
                    <span class="tag">Période {e.period}</span>
                  </div>
                  <div style={{ fontSize: '1.45rem', fontWeight: 900 }}>
                    {e.score === 'A' || e.score === 'a' ? 'Absent' : `${e.score} / ${e.max}`}
                  </div>
                </div>
              ))
          ) : (
            <div class="empty">Aucune évaluation.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The pupil's observations as a paper document: identity header, then one row
 * per note, oldest first.
 *
 * Rendered next to the file rather than inside it, and hidden on screen
 * (`print.css` is what reveals it): the screen wants cards side by side with
 * the evaluations, paper wants a single table that can run over several pages.
 */
function ObservationSheet({ student }: { student: Student }) {
  const observations = live(useStore((s) => s.observations)).filter(
    (o) => o.studentId === student.id,
  );
  const lessons = live(useStore((s) => s.lessons));
  const settings = useStore((s) => s.settings);

  const rows = useMemo(() => observationRows(observations, lessons), [observations, lessons]);
  const period = observationPeriod(rows);

  return (
    <section class="obsSheet">
      <header class="obsSheetHead">
        <div>
          <p class="obsSheetKicker">Journal d’observations</p>
          <h2>{studentDisplayName(student.name)}</h2>
        </div>
        <dl class="obsSheetMeta">
          <div>
            <dt>Classe</dt>
            <dd>{settings.className || '—'}</dd>
          </div>
          <div>
            <dt>Année</dt>
            <dd>{settings.schoolYear || '—'}</dd>
          </div>
          <div>
            <dt>Titulaire</dt>
            <dd>{settings.teacher || '—'}</dd>
          </div>
          <div>
            <dt>Né(e) le</dt>
            <dd>{frSheetDate(student.birth) || '—'}</dd>
          </div>
          <div>
            <dt>Philosophie</dt>
            <dd>{student.philo || '—'}</dd>
          </div>
          <div>
            <dt>Observations</dt>
            <dd>
              {rows.length}
              {period ? ` — ${period}` : ''}
            </dd>
          </div>
        </dl>
      </header>

      {rows.length ? (
        <table class="obsSheetTable">
          <thead>
            <tr>
              <th scope="col" class="obsSheetDate">Date</th>
              <th scope="col">Matière</th>
              <th scope="col">Point d’attention</th>
              <th scope="col">Observation</th>
              <th scope="col">Aide / remédiation</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td class="obsSheetDate">{row.date || '—'}</td>
                <td>
                  {row.subject}
                  {row.lesson && <div class="small muted">{row.lesson}</div>}
                </td>
                <td>{row.difficulty || '—'}</td>
                <td>
                  {row.note && <div>{row.note}</div>}
                  {/* A stylus note is often the whole observation, so the sheet
                      would be blank without the image. */}
                  {row.hasHandwriting && <Handwriting observationId={row.id} />}
                </td>
                <td>{row.support || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p class="empty">Aucune observation à imprimer.</p>
      )}
    </section>
  );
}

export function ClassPage() {
  const students = live(useStore((s) => s.students));
  const selectedId = useStore((s) => s.selectedStudentId);
  const [view, setView] = useState<'students' | 'evaluations'>('students');
  const [query, setQuery] = useState('');

  const selected = students.find((s) => s.id === selectedId);
  // Alphabetical, so the numbers match the school's own class list; recomputed
  // from the class rather than stored, so an arrival or a departure renumbers
  // everyone at once.
  const numbers = useMemo(() => studentNumbers(students), [students]);
  const roster = useMemo(
    () =>
      [...students].sort((a, b) => (numbers.get(a.id) ?? 0) - (numbers.get(b.id) ?? 0)),
    [students, numbers],
  );

  return (
    // `printGrid` only when the table is what is on screen: printing a pupil's
    // file prints their observation sheet instead, which is a portrait page
    // with rules of its own.
    <section class={selected ? 'page printObservations' : 'page printGrid'}>
      <div class="sectionTitle">
        <div>
          <h1>Ma classe</h1>
          <p class="muted">
            Encode tes élèves ici. Ensuite observations et évaluations se rangent automatiquement
            dans leur dossier.
          </p>
        </div>
        <div class="row">
          <button
            class="btn soft"
            onClick={() =>
              openModal('Évaluation d’une leçon', <LessonEvaluationModal />)
            }
          >
            📊 Évaluation d’une leçon
          </button>
          <button
            class="btn soft"
            onClick={() => openModal('Importer une liste d’élèves', <BulkStudentsModal />)}
          >
            Importer une liste
          </button>
          <button
            class="btn primary"
            onClick={() => openModal('Ajouter un élève', <StudentModal studentId={null} />)}
          >
            + Ajouter un élève
          </button>
        </div>
      </div>

      {!selected && (
        <div class="classTabs" role="tablist" aria-label="Vue de la classe">
          <button
            class={view === 'students' ? 'active' : undefined}
            role="tab"
            aria-selected={view === 'students'}
            onClick={() => setView('students')}
          >
            Liste des élèves <span>{students.length}</span>
          </button>
          <button
            class={view === 'evaluations' ? 'active' : undefined}
            role="tab"
            aria-selected={view === 'evaluations'}
            onClick={() => setView('evaluations')}
          >
            Évaluations
          </button>
        </div>
      )}

      {selected ? (
        <>
          <StudentDetail student={selected} number={numbers.get(selected.id)} />
          <ObservationSheet student={selected} />
        </>
      ) : view === 'evaluations' ? (
        <ClassGrid />
      ) : (
        <div class="studentListCard">
          <input
            type="search"
            aria-label="Rechercher un élève"
            placeholder="Rechercher un élève…"
            value={query}
            onInput={(event) => setQuery(event.currentTarget.value)}
          />
          <div class="studentList">
            {roster
              .filter((student) =>
                studentDisplayName(student.name)
                  .toLocaleLowerCase('fr')
                  .includes(query.trim().toLocaleLowerCase('fr')),
              )
              .map((student) => (
                <button key={student.id} onClick={() => useStore.getState().selectStudent(student.id)}>
                  <span class="avatar">{numbers.get(student.id)}</span>
                  <span>
                    <b>{studentDisplayName(student.name)}</b>
                    <small>Ouvrir le dossier</small>
                  </span>
                  <span aria-hidden="true">›</span>
                </button>
              ))}
          </div>
          {!students.length && <div class="empty">Aucun élève. Ajoute-les ou importe une liste depuis tes fichiers.</div>}
        </div>
      )}
    </section>
  );
}
